import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import { ja } from './locales/ja'
import { en } from './locales/en'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, detectBrowserLocale, readStoredLocale, storeLocale, type Locale } from './locale'

export { DEFAULT_LOCALE, LOCALE_NATIVE_NAMES, SUPPORTED_LOCALES, isLocale, type Locale } from './locale'

/**
 * i18next の初期化。
 *
 * import しただけで初期化される副作用モジュールにしてある。`I18nextProvider` を
 * 置かず**グローバルインスタンス**に寄せているのは、既存の 400 本近いテストが
 * それぞれ素の `render()` を呼んでいるためで、Provider 必須にすると全テストの
 * 書き換えが要る。読み込み口は次の 3 箇所だけにする。
 *
 * - `src/index.tsx`（アプリ本体）
 * - `src/setupTests.ts`（vitest）
 * - `.storybook/preview.tsx`（Storybook / VRT）
 *
 * `initAsync: false` と `react.useSuspense: false` は、初回描画を同期で
 * 確定させるため（i18next 24 で `initImmediate` から改名された）。非同期に
 * すると翻訳前の空文字が 1 フレーム描画され、VRT が中身の無い絵を baseline に
 * してしまう。
 *
 * 初期言語はブラウザに保存した選択（`readStoredLocale`）で、無ければ日本語。
 * アプリ本体ではこの後 `applyBrowserLocale` がブラウザの言語設定を既定にする。
 * en に無いキー（法務文書など）は fallbackLng の日本語を出す。
 */
if (!i18next.isInitialized) {
  void i18next.use(initReactI18next).init({
    lng: readStoredLocale() ?? DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    resources: { ja: { translation: ja }, en: { translation: en } },
    // 値の埋め込みは React が描画時にエスケープするので二重にしない。
    interpolation: { escapeValue: false },
    initAsync: false,
    react: { useSuspense: false },
    // 未定義キーはキー文字列がそのまま画面へ出る。E2E のロケータは表示文言で
    // 組まれている（`.claude/rules/e2e.md`）ため、`clip.foo.title` のような
    // 文字列が出た時点で spec が壊れる。ESLint の
    // `jizaipad-policy/i18n-key-must-exist` が CI 前に落とす前提にし、
    // 実行時はログだけ残す。
    // missingKeyHandler は saveMissing が真のときだけ呼ばれる。書き戻す backend は
    // 繋いでいないので、実質「未定義キーを検知したら通知する」だけの設定になる。
    saveMissing: true,
    missingKeyHandler (_lngs, _ns, key) {
      console.error(`[i18n] 未定義の翻訳キー: ${key}`)
    }
  })
}

export { i18next }

/**
 * 表示言語を切り替え、ブラウザ（localStorage）に保存する。
 * 選んだ言語はブラウザごとの設定で、ユーザーやサーバーには保存しない（TM-674）。
 */
export const applyLocale = (locale: Locale): void => {
  storeLocale(locale)
  document.documentElement.lang = locale
  if (i18next.language !== locale) {
    // リソースは同梱なので同期で切り替わる。失敗は握りつぶさずログに残す。
    i18next.changeLanguage(locale).catch((error: unknown) => {
      console.error('[i18n] 表示言語を切り替えられませんでした:', error)
    })
  }
}

/**
 * 言語を選んだことが無いブラウザでは、ブラウザの言語設定で表示する（TM-674）。
 * 既定はブラウザのシステム設定に従い、アカウントメニューから任意に変えられる。
 *
 * **アプリ本体（`src/index.tsx`）からだけ呼ぶ。** vitest（jsdom）や Storybook / VRT の
 * ブラウザは en-US を返すため、ここで切り替えると日本語前提のテストと baseline が英語になる。
 * localStorage には書かない。ブラウザの言語設定を変えたら、次の読み込みからそれに追従させるため。
 */
export const applyBrowserLocale = (): void => {
  // 読み上げやブラウザの翻訳機能が表示言語を正しく判定できるよう、html lang も合わせる。
  const stored = readStoredLocale()
  if (stored != null) {
    document.documentElement.lang = stored
    return
  }
  const detected = detectBrowserLocale(navigator.languages ?? [navigator.language])
  if (detected == null) return
  document.documentElement.lang = detected
  if (i18next.language !== detected) {
    i18next.changeLanguage(detected).catch((error: unknown) => {
      console.error('[i18n] 表示言語を切り替えられませんでした:', error)
    })
  }
}

export { Trans, useTranslation } from 'react-i18next'

/**
 * React の外（yup スキーマ、定数テーブル、util）から翻訳する。
 *
 * **モジュールのトップレベルで即時に呼ばない。** 初期化前に評価されると
 * キー文字列がそのまま焼き付き、言語切替でも更新されなくなる。
 * 定数テーブルやスキーマは「関数にして呼び出し時に評価する」形にする。
 */
export const t = i18next.t.bind(i18next)
